/**
 * Leave Types Edit Page — hardcoded form for editing a leave type.
 */

import * as React from 'react';
import { useParams, useNavigate } from 'react-router';
import { useOne, useUpdate } from '@refinedev/core';
import {
  Button, Card, CardContent,
  Input, Label, Textarea, Checkbox,
} from '@yesbos/ui';
import { ArrowLeft } from 'lucide-react';

export function LeaveTypesEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading: fetchLoading } = useOne({ resource: 'leave_types', id: id! });
  const { mutate: update, isLoading: updateLoading } = useUpdate();

  const [form, setForm] = React.useState<Record<string, unknown> | null>(null);

  React.useEffect(() => {
    if (data?.data) setForm({ ...data.data });
  }, [data]);

  function set(field: string, value: unknown) {
    setForm(prev => prev ? { ...prev, [field]: value } : prev);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form) return;
    const { id: _id, org_id, created_at, updated_at, ...values } = form as Record<string, unknown>;
    update(
      { resource: 'leave_types', id: id!, values },
      { onSuccess: () => navigate(`/leave-types/${id}`) },
    );
  }

  if (fetchLoading || !form) return <div className="p-6 text-sm text-muted-foreground">Loading...</div>;

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/leave-types/${id}`)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-semibold">Edit Leave Type</h1>
      </div>

      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="name">Name *</Label>
                <Input id="name" value={String(form.name ?? '')} onChange={e => set('name', e.target.value)} required />
              </div>
              <div className="space-y-1">
                <Label htmlFor="code">Code *</Label>
                <Input id="code" value={String(form.code ?? '')} onChange={e => set('code', e.target.value.toUpperCase())} required />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="annual_quota">Annual Quota (days)</Label>
                <Input id="annual_quota" type="number" min={0} value={Number(form.annual_quota ?? 0)} onChange={e => set('annual_quota', parseInt(e.target.value) || 0)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="max_consecutive_days">Max Consecutive Days</Label>
                <Input id="max_consecutive_days" type="number" min={0} value={form.max_consecutive_days !== null && form.max_consecutive_days !== undefined ? String(form.max_consecutive_days) : ''} onChange={e => set('max_consecutive_days', e.target.value)} placeholder="Unlimited" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="min_service_days">Min Service Days</Label>
                <Input id="min_service_days" type="number" min={0} value={Number(form.min_service_days ?? 0)} onChange={e => set('min_service_days', parseInt(e.target.value) || 0)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="applicable_gender">Applicable Gender</Label>
                <select
                  id="applicable_gender"
                  className="w-full border border-input bg-background rounded-md px-3 py-2 text-sm"
                  value={String(form.applicable_gender ?? '')}
                  onChange={e => set('applicable_gender', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="max_carry_forward">Max Carry Forward Days</Label>
                <Input id="max_carry_forward" type="number" min={0} value={Number(form.max_carry_forward ?? 0)} onChange={e => set('max_carry_forward', parseInt(e.target.value) || 0)} />
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" value={String(form.description ?? '')} onChange={e => set('description', e.target.value)} rows={3} />
            </div>

            <div className="flex flex-wrap gap-6 pt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={Boolean(form.is_paid)} onCheckedChange={v => set('is_paid', v)} />
                <span className="text-sm">Paid Leave</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={Boolean(form.is_carry_forward)} onCheckedChange={v => set('is_carry_forward', v)} />
                <span className="text-sm">Carry Forward</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={Boolean(form.is_encashable)} onCheckedChange={v => set('is_encashable', v)} />
                <span className="text-sm">Encashable</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={Boolean(form.is_active)} onCheckedChange={v => set('is_active', v)} />
                <span className="text-sm">Active</span>
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => navigate(`/leave-types/${id}`)}>Cancel</Button>
              <Button type="submit" disabled={updateLoading}>{updateLoading ? 'Saving...' : 'Save Changes'}</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default LeaveTypesEditPage;
